import React from 'react';
import {
	Box,
	Flex,
	Heading,
	Spacer,
	HStack,
	VStack,
	Text,
	Button,
	Image,
	Portal,
	Dialog,
	CloseButton
} from '@chakra-ui/react';
import moment from 'moment';
import theme from '../theme';
import axios from 'axios';
import * as Sentry from '@sentry/react';

// https://stackoverflow.com/questions/43432892/force-download-get-request-using-axios
const handleExport = (record, project_number) =>
	axios
		.get('/generatedriverexemption', {
			params: { record: { ...record, project_number } },
			responseType: 'arraybuffer'
		})
		.then(response => {
			const fileName = response.headers['content-disposition'].split('=')[1].slice(0, -1);
			const pdf = new File([response.data], fileName, { type: response.headers['content-type'] });
			// https://stackoverflow.com/questions/41947735/custom-name-for-blob-url
			const fileLink = document.createElement('a');

			fileLink.href = window.URL.createObjectURL(pdf);
			fileLink.download = fileName;
			fileLink.click();
			fileLink.remove();
			window.URL.revokeObjectURL(fileLink.href);
		})
		.catch(error => {
			Sentry.captureException(error);
			throw error;
		});

export default ({ record, currentProject }) => {
	const [exporting, setExporting] = React.useState(false);
	const [errorDialog, setErrorDialog] = React.useState(false);

	const errorModalRef = React.useRef();

	return (
		<Box p={4} mb={6}>
			<Dialog.Root
				role="alertdialog"
				initialFocusEl={errorModalRef}
				onOpenChange={({ open }) => {
					if (!open) {
						setErrorDialog(null);
					}
				}}
				open={errorDialog}
				placement="center"
			>
				<Portal>
					<Dialog.Backdrop />
					<Dialog.Positioner>
						<Dialog.Content opacity="1 !important">
							<Dialog.Header>
								<Dialog.Title fontSize="lg" fontWeight="bold">
									Error Exporting Driver Exemption
								</Dialog.Title>
							</Dialog.Header>
							<Dialog.Body>
								<p>{errorDialog}</p>
							</Dialog.Body>
							<Dialog.Footer>
								<Dialog.ActionTrigger asChild>
									<Button
										ref={errorModalRef}
										ml={3}
										color={theme.colors.white}
										bg={theme.colors.blue[700]}
										_hover={{ backgroundColor: theme.colors.blue[800] }}
										onClick={() => setErrorDialog(null)}
									>
										Okay
									</Button>
								</Dialog.ActionTrigger>
							</Dialog.Footer>
							<Dialog.CloseTrigger asChild>
								<CloseButton size="sm" />
							</Dialog.CloseTrigger>
						</Dialog.Content>
					</Dialog.Positioner>
				</Portal>
			</Dialog.Root>
			<HStack spacing={0} justify="center" align="flex-end" p={2}>
				<Flex minW="230px" align="flex-end" h="100%">
					<Image src="/themes/custom/msiforms/images/logo.png" objectFit="contain" />
				</Flex>
				<Spacer />
				<VStack spacing="8px" mb="24px" align="flex-end" minW="235px">
					<Heading size="lg" pt="24px">
						Hours of Service
					</Heading>
					<Heading size="lg">Exemption Form</Heading>
				</VStack>
				<Spacer />
				<Flex justify="flex-end" minW="230px">
					<Button
						isLoading={exporting}
						loadingText="Exporting"
						color={theme.colors.white}
						bg={theme.colors.blue[700]}
						_hover={{ backgroundColor: theme.colors.blue[800] }}
						onClick={() => {
							setExporting(true);
							handleExport(record, currentProject)
								.then(() => setExporting(false))
								.catch(error => setErrorDialog(error.message));
						}}
					>
						Export Form
					</Button>
				</Flex>
			</HStack>
			<VStack p={2} spacing="20px">
				<Text>
					Complete the fields below during any projects involving work on public utilities (electric, gas,
					water, sanitary sewer, telephone, etc.) and keep this form in the vehicle with you at all times. If
					you receive a DOT inspection (roadside or weigh-station) present this document in addition to your
					daily DOT driving logs to the inspecting officer. The explanation provided below details our
					utilization of the Utility Service Vehicle Hours of Service Exemption. This does not exempt anyone
					from logging DOT drives thru Keep Truckin, so please continue to correctly log all your on-duty
					time.
				</Text>
				<VStack spacing="20px" w="80%" minW="560px">
					<HStack spacing="15px" w="100%">
						<Flex flex="1" justify="flex-end" pb="6px">
							<Text>Exemption Start/Finish Date:</Text>
						</Flex>
						<Flex flex="1" pb="5px" borderBottom="1px solid black">
							<Text ml="5px">
								{record.est_start_date
									? moment(record.est_start_date * 1000).format('MM/DD/YYYY')
									: 'N/A'}{' '}
								-{' '}
								{record.est_end_date ? moment(record.est_end_date * 1000).format('MM/DD/YYYY') : 'N/A'}
							</Text>
						</Flex>
					</HStack>
					<HStack spacing="15px" w="100%">
						<Flex flex="1" justify="flex-end" pb="6px">
							<Text>Driver Name:</Text>
						</Flex>
						<Flex flex="1" pb="5px" borderBottom="1px solid black">
							<Text ml="5px">{record.name || ''}</Text>
						</Flex>
					</HStack>
					<HStack spacing="15px" w="100%">
						<Flex flex="1" justify="flex-end" pb="6px">
							<Text>Manager Name:</Text>
						</Flex>
						<Flex flex="1" pb="5px" borderBottom="1px solid black">
							<Text ml="5px">{record.marine_solutions_project_manager || ''}</Text>
						</Flex>
					</HStack>
					<HStack spacing="15px" w="100%">
						<Flex flex="1" justify="flex-end" pb="6px">
							<Text>Client/Utility Provider:</Text>
						</Flex>
						<Flex flex="1" pb="5px" borderBottom="1px solid black">
							<Text ml="5px">{record.client_name || ''}</Text>
						</Flex>
					</HStack>
					<HStack spacing="15px" w="100%">
						<Flex flex="1" justify="flex-end" pb="6px">
							<Text>Reason for being over Hours of Service:</Text>
						</Flex>
						<Flex flex="1" pb="5px" borderBottom="1px solid black">
							<Text ml="5px">Work performed on Public Utility</Text>
						</Flex>
					</HStack>
					<HStack spacing="15px" w="100%">
						<Flex flex="1" justify="flex-end" pb="6px">
							<Text>Listed Exemption:</Text>
						</Flex>
						<Flex flex="1" pb="5px" borderBottom="1px solid black">
							<Text ml="5px">Commercial Vehicle (CMV) Hours of Service (HOS)</Text>
						</Flex>
					</HStack>
				</VStack>
				<Text>
					49 CFR SECTION: 395.1 (n) Utility service vehicles. The provisions of this part shall not apply to a
					driver of a utility service vehicle as defined in §395.2. 395.2 Utility service vehicle means any
					commercial motor vehicle:
				</Text>
				<Text>
					(1) Used in the furtherance of repairing, maintaining, or operating any structures or any other
					physical facilities necessary for the delivery of public utility services, including the furnishing
					of electric, gas, water, sanitary sewer, telephone, and television cable or community antenna
					service;
				</Text>
				<Text>
					(2) While engaged in any activity necessarily related to the ultimate delivery of such public
					utility services to consumers, including travel or movement to, from, upon, or between activity
					sites (including occasional travel or movement outside the service area necessitated by any utility
					emergency as determined by the utility provider); and
				</Text>
				<Text>
					(3) Except for any occasional emergency use, operated primarily within the service area of a
					utility&apos;s subscribers or consumers, without regard to whether the vehicle is owned, leased, or
					rented by the utility.
				</Text>
				<VStack spacing="10px" align="flex-start" w="100%">
					<Text px="5px">Description of Activities/Project Overview:</Text>
					<Box p="10px 5px" w="100%" borderY="1px solid black">
						<Text>{record.project_steps || <br />}</Text>
					</Box>
				</VStack>
				<VStack spacing="10px" align="center">
					<Text fontWeight="bold" textAlign="center" fontSize="17px">
						KEEP A COPY OF THIS LETTER WITH THE DRIVER FOR 8 DAYS AFTER THE EXEMPTION IS USED. PLEASE ENSURE
						THIS FORM OR A COPY IS DELIVERED TO ROBERT FISHER.
					</Text>
					<Text fontWeight="bold" textAlign="center" color={theme.colors.blue[700]}>
						225 Industry Parkway, Nicholasville, KY 40356 | 859.260.1055
					</Text>
					<Text fontWeight="bold" textAlign="center" color={theme.colors.blue[700]}>
						www.MSImarinesolutions.com
					</Text>
				</VStack>
			</VStack>
		</Box>
	);
};
